import { CustomerPortalInputType, SubdomainType } from '@/constants'
import { useSessionStore } from '@/stores'
import { portalEntryPath } from '@/components/OrderManagementPortal/utils/portalNav'

// Single source of truth for portal routing — see docs/portal-architecture.md.
export interface PortalDescriptor {
  id: string
  subdomainType?: SubdomainType
  ownsStepTypes?: number[]
  homePath: (session: any) => string
  // Set only when the portal owns the prefix exclusively; unset = funnel shares it.
  exclusiveBasePath?: string
}

export const FUNNEL_HOME = '/session'

export const PORTALS: PortalDescriptor[] = [
  {
    id: 'omp',
    subdomainType: SubdomainType.CustomerPortal,
    ownsStepTypes: Object.values(CustomerPortalInputType) as number[],
    homePath: session => portalEntryPath(session),
    exclusiveBasePath: '/portal',
  },
  {
    id: 'marketplace',
    subdomainType: SubdomainType.Marketplace,
    homePath: session => (session?.show_marketplace ? '/products' : FUNNEL_HOME),
  },
]

// A popup is chrome over the step underneath; background clones are the step itself.
function isTransientPopup(session: any): boolean {
  return !!session?.is_popup && !session?.is_background_session
}

// Order matters: a later subdomain match must not beat an earlier step-type match.
// step_type only — matching valid_inputs[0].type would break the isCustomerPortal subset.
function claims(portal: PortalDescriptor, session: any, subdomainType: number | null): boolean {
  if (portal.subdomainType != null && subdomainType === portal.subdomainType) {
    return true
  }
  if (!portal.ownsStepTypes?.length || !session || isTransientPopup(session)) {
    return false
  }
  return portal.ownsStepTypes.includes(Number(session.step_type))
}

export function resolvePortal(session: any, campaign: any): PortalDescriptor | null {
  const subdomainType = campaign?.subdomain_type != null ? Number(campaign.subdomain_type) : null
  return PORTALS.find(portal => claims(portal, session, subdomainType)) ?? null
}

export function sessionHomePath(session: any, campaign: any): string {
  return resolvePortal(session, campaign)?.homePath(session) ?? FUNNEL_HOME
}

// Back to the session renderer: only an exclusiveBasePath portal diverts, never the
// marketplace — /products re-fires its jump and skips the user's step.
export function sessionReturnPath(session: any, campaign: any): string {
  const portal = resolvePortal(session, campaign)
  return portal?.exclusiveBasePath ? portal.homePath(session) : FUNNEL_HOME
}

// Live store read: the action just taken may have changed which portal owns the session.
export function currentSessionHome(): string {
  const { session, campaign } = useSessionStore.getState()
  return sessionHomePath(session, campaign)
}

export function currentSessionReturn(): string {
  const { session, campaign } = useSessionStore.getState()
  return sessionReturnPath(session, campaign)
}

function isUnder(pathname: string, basePath: string): boolean {
  return pathname === basePath || pathname.startsWith(`${basePath}/`)
}

export function portalOwningPath(pathname: string): PortalDescriptor | null {
  return (
    PORTALS.filter(p => p.exclusiveBasePath && isUnder(pathname, p.exclusiveBasePath)).sort(
      (a, b) => (b.exclusiveBasePath as string).length - (a.exclusiveBasePath as string).length
    )[0] ?? null
  )
}

export function portalRedirectFor(pathname: string, session: any, campaign: any): string | null {
  const portal = resolvePortal(session, campaign)
  const owner = portalOwningPath(pathname)

  if (portal === owner) {
    return null
  }
  if (portal?.exclusiveBasePath) {
    const target = portal.homePath(session)
    // A home outside its own prefix would bounce between loaders forever.
    return isUnder(target, portal.exclusiveBasePath) ? target : portal.exclusiveBasePath
  }
  if (owner) {
    return sessionReturnPath(session, campaign)
  }
  return null
}
